<template>
  <div v-if="grupo" class="unidade-selector" @click.stop>
    <button
      class="unidade-btn"
      @click="aberto = !aberto"
      :title="unidadeAtual?.nome || 'Selecionar unidade'"
    >
      <Building2 class="icon-sm" />
      <span class="unidade-nome">{{ unidadeAtual?.nome || "Unidade" }}</span>
      <ChevronDown class="chevron" :class="{ aberto }" />
    </button>

    <transition name="dropdown-fade">
      <div v-if="aberto" class="unidade-dropdown">
        <div class="unidade-header">{{ grupo.nomeGrupo }}</div>
        <button
          v-for="u in grupo.unidades"
          :key="u.idEmpresa"
          class="unidade-item"
          :class="{ ativa: u.idEmpresa === idEmpresaAtual }"
          @click="trocarUnidade(u)"
        >
          <span class="unidade-item-nome">{{ u.nome }}</span>
          <span class="unidade-item-cidade">{{ u.cidade }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Building2, ChevronDown } from "lucide-vue-next";
import { useGlobalStore } from "@/store/useGlobalStore";
import apiClient from "@/services/apiClient";

const store = useGlobalStore();
const aberto = ref(false);
const grupo = ref(null);
const _jaCarregou = ref(false);

const idEmpresaAtual = computed(() => store.empresaLogada?.idEmpresa);
const unidadeAtual = computed(() =>
  grupo.value?.unidades?.find((u) => u.idEmpresa === idEmpresaAtual.value),
);

async function carregarGrupo() {
  // Só chama se: funcionalidades já carregadas (length > 0) E tem multi_unidade
  // Evita chamada desnecessária quando funcionalidades[] ainda está vazio no startup
  // (nesse estado temFuncionalidade retorna true como fallback → falso positivo)
  if (store.funcionalidades.length === 0) return;
  if (!store.temFuncionalidade("multi_unidade")) return;
  const id = store.empresaLogada?.idEmpresa;
  if (!id) return;
  if (_jaCarregou.value) return;
  _jaCarregou.value = true;
  try {
    const res = await apiClient.get(`EmpresaGrupo/PorEmpresa/${id}`);
    grupo.value = res.data;
  } catch {
    grupo.value = null;
  }
}

// Aguarda as funcionalidades serem carregadas da API antes de tentar a chamada
watch(
  () => store.funcionalidades.length,
  (len) => {
    if (len > 0) carregarGrupo();
  },
);

// Aguarda empresa ser definida (ex: reload onde empresa carrega depois dos menus)
watch(
  () => store.empresaLogada?.idEmpresa,
  () => carregarGrupo(),
);

async function trocarUnidade(unidade) {
  aberto.value = false;
  try {
    const res = await apiClient.get(
      `Empresa/BuscarEmpresasVinculadoAoUsuario?idUsuario=${store.empresaLogada?.idUsuario}`,
    );
    const emp = res.data?.find((e) => e.idEmpresa === unidade.idEmpresa);
    if (emp) {
      store.empresaLogada = emp;
    }
  } catch (err) {
    console.error("Erro ao trocar unidade", err);
  }
}

function fecharFora(e) {
  if (!e.target.closest(".unidade-selector")) aberto.value = false;
}

onMounted(() => {
  carregarGrupo();
  document.addEventListener("click", fecharFora);
});
onUnmounted(() => document.removeEventListener("click", fecharFora));
</script>

<style scoped>
.unidade-selector {
  position: relative;
}

.unidade-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  color: inherit;
  font-size: 0.85rem;
  transition: background 0.15s;
}
.unidade-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}
.unidade-nome {
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.icon-sm {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.2s;
}
.chevron.aberto {
  transform: rotate(180deg);
}

.unidade-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  overflow: hidden;
  z-index: 1000;
}
.unidade-header {
  padding: 10px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  background: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
}
.unidade-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.12s;
}
.unidade-item:hover {
  background: #f3f4f6;
}
.unidade-item.ativa {
  background: #ede9fe;
}
.unidade-item-nome {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}
.unidade-item-cidade {
  font-size: 0.78rem;
  color: #6b7280;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
